import React, { useEffect, useState } from 'react'
import { logoExists } from './logoUtils'
import { getCompanySettings } from './companyUtils'
import LoginForm from './LoginForm'
import { login } from './service/api'
import AssignedBuildings from './AssignedBuildings'
import ApartmentsList from './ApartmentsList'
import ApartmentVisitForm from './ApartmentVisitForm'
import { clearToken, getToken } from './auth'
import { invalidateOrderPestsCache } from './apartmentPestsApi'
import InstallPWA from './InstallPWA'
import LogoutIcon from './components/LogoutIcon'
import UTurnArrowIcon from './components/UTurnArrowIcon'
import './App.css'

export default function App() {

   /* ==============================
      IDIOMA
   ============================== */
   const getInitialLang = () => {
      const stored = window.localStorage.getItem('lang')
      if (stored) return stored
      window.localStorage.setItem('lang', 'en')
      return 'en'
   }

   const [lang, setLang] = useState(getInitialLang)

   const setLangAndStore = (newLang) => {
      setLang(newLang)
      window.localStorage.setItem('lang', newLang)
   }

   /* ==============================
      ESTADOS GENERALES
   ============================== */
   const [showLogo, setShowLogo] = useState(false)
   const [companyName, setCompanyName] = useState('Technician App')
   const [isAuthenticated, setIsAuthenticated] = useState(!!getToken())
   const [loginError, setLoginError] = useState('')
   
   // Recuperar estados de navegación desde localStorage
   const [selectedOrder, setSelectedOrder] = useState(() => {
      try {
         const saved = localStorage.getItem('pwa_selectedOrder')
         return saved ? JSON.parse(saved) : null
      } catch {
         return null
      }
   })
   
   const [selectedApartment, setSelectedApartment] = useState(() => {
      try {
         const saved = localStorage.getItem('pwa_selectedApartment')
         return saved ? JSON.parse(saved) : null
      } catch {
         return null
      }
   })
   const [selectedApartmentPests, setSelectedApartmentPests] = useState([])
   
   const [visitKey, setVisitKey] = useState(0)
   const [companySettings, setCompanySettings] = useState(null)

   /* ==============================
      CARGA INICIAL
   ============================== */
   useEffect(() => {
      logoExists().then(setShowLogo)

      const token = getToken()

      if (token) {
         setIsAuthenticated(true)
         loadCompanySettings()
      }

   }, [])

   // Persistir selectedOrder en localStorage
   useEffect(() => {
      if (selectedOrder) {
         localStorage.setItem('pwa_selectedOrder', JSON.stringify(selectedOrder))
      } else {
         localStorage.removeItem('pwa_selectedOrder')
      }
   }, [selectedOrder])

   // Persistir selectedApartment en localStorage
   useEffect(() => {
      if (selectedApartment) {
         localStorage.setItem('pwa_selectedApartment', JSON.stringify(selectedApartment))
      } else {
         localStorage.removeItem('pwa_selectedApartment')
      }
   }, [selectedApartment])

   /* ==============================
      FUNCIONES
   ============================== */
   const loadCompanySettings = async () => {
      try {
         const settings = await getCompanySettings()

         if (!settings) return

         setCompanySettings(settings)

         if (settings.name) setCompanyName(settings.name)
         if (settings.language) setLangAndStore(settings.language)

         // console.log("Settings loaded:", settings)

      } catch (e) {
         console.error("Error loading settings:", e)
      }
   }

   const handleLogin = async (username, password) => {

      setLoginError('')

      try {
         await login(username, password)

         let retries = 0
         let token = getToken()

         while (!token && retries < 10) {
            await new Promise(res => setTimeout(res, 50))
            token = getToken()
            retries++
         }

         if (!token) {
            setLoginError('Token not found after login')
            return
         }

         setIsAuthenticated(true)
         await loadCompanySettings()

      } catch (e) {
         setLoginError(e.message)
      }
   }

   const handleSelectApartment = (apartment, pests = []) => {
      setSelectedApartment(apartment)
      setSelectedApartmentPests(Array.isArray(pests) ? pests : [])
   }

   const handleLogout = () => {
      clearToken()
      if (selectedOrder?.id) {
         invalidateOrderPestsCache(selectedOrder.id)
      }
      setIsAuthenticated(false)
      setSelectedOrder(null)
      setSelectedApartment(null)
      setSelectedApartmentPests([])
      setLoginError('')
   }

   const handleBackToOrders = () => {
      // No invalidamos la caché de plagas al navegar dentro de la misma orden.
      // La caché se revalida por orderVersion/TTL; esto evita recargar la misma data innecesariamente.
      setSelectedOrder(null)
      setSelectedApartment(null)
      setSelectedApartmentPests([])
   }

   const handleVisitSuccess = () => {
      // No borrar la caché de plagas al volver a la lista; se reutiliza mientras la orden
      // siga siendo la misma y se revalida automáticamente por versión y TTL.
      setSelectedApartment(null)
      setSelectedApartmentPests([])
      setVisitKey(k => k + 1)
   }

   /* ==============================
      RENDER
   ============================== */
   return (
      <div className="main" lang={lang}>

         {/* HEADER */}
         {isAuthenticated && (
            <header className="header">
               {selectedOrder && !selectedApartment && (
                 <button 
                  type="button" 
                  onClick={handleBackToOrders} 
                  className="weight700 header-button"
                 ><UTurnArrowIcon/></button>
               )}
               {selectedOrder && selectedApartment && (
                 <button 
                  type="button" 
                  onClick={() => setSelectedApartment(null)} 
                  className="weight700 header-button">
                     <UTurnArrowIcon/></button>
               )}
               <div className="company-header">
                  {showLogo && (
                     <img src="/company/logo.webp" alt="Logo" loading='lazy' style={{ height: 40 }} />
                  )}
                  {companyName}
               </div>

               <button
                  onClick={handleLogout}
                  className="weight700 header-button"
                  title={lang === 'en' ? 'Logout' : 'Cerrar sesión'} >
                  <LogoutIcon width={24} height={24} color="white" />
               </button>
            </header>
         )}

         {/* MAIN */}
         <main style={{ flex: 1, padding: '0 16px' }}>
            {!isAuthenticated ? (
               <div style={{ textAlign: 'center', marginTop: 40 }}>
                  <h2>{lang === 'en' ? 'Welcome' : 'Bienvenido'}</h2>
                  {showLogo && (
                     <img
                        src="/company/logo.webp"
                        srcSet="/company/logo.webp 120w, /company/logo-2x.webp 240w"
                        sizes="(max-width: 600px) 120px, 240px"
                        alt="Logo"
                        width={114}
                        height={120}
                        style={{ height: 120, marginBottom: 24 }}
                     />
                  )}
                  <LoginForm onLogin={handleLogin} error={loginError} lang={lang} />
               </div>
            ) : !selectedOrder ? (

               <AssignedBuildings onSelect={setSelectedOrder} lang={lang} />

            ) : !selectedApartment ? (

               <ApartmentsList
                  order={selectedOrder}
                  onSelect={handleSelectApartment}
                  lang={lang}
                  onBack={handleBackToOrders}
                  onOrderCompleted={handleBackToOrders}
               />

            ) : (

               <ApartmentVisitForm
                  key={visitKey}
                  order={selectedOrder}
                  apartment={selectedApartment}
                  pests={selectedApartmentPests}
                  lang={lang}
                  onSuccess={handleVisitSuccess}
                  onBack={() => {
                     setSelectedApartment(null)
                     setSelectedApartmentPests([])
                  }}
               />

            )}
            {/* Prompt de instalación de PWA */}
            <InstallPWA />
         </main>
      </div>
   )
}
